import { useMemo } from "react";

/**
 * SignalField — static SVG "signal through noise" dot field.
 * Deterministic random points generated from a fixed seed for SSR-stable output.
 */
function seeded(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

export default function SignalField({ density = 220, className = "" }) {
  const points = useMemo(() => {
    const rng = seeded(42);
    const pts = [];
    for (let i = 0; i < density; i++) {
      const x = rng() * 1200;
      const y = rng() * 700;
      // Cluster brightness — sparse bright points within faint noise
      const isBright = rng() > 0.92;
      const isMid = !isBright && rng() > 0.75;
      const r = isBright ? 1.6 + rng() * 1.2 : isMid ? 1.0 + rng() * 0.5 : 0.4 + rng() * 0.4;
      const opacity = isBright
        ? 0.85 + rng() * 0.15
        : isMid
          ? 0.45 + rng() * 0.25
          : 0.12 + rng() * 0.18;
      pts.push({ x, y, r, opacity, bright: isBright });
    }
    return pts;
  }, [density]);

  return (
    <svg
      className={className}
      viewBox="0 0 1200 700"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="vignette" cx="50%" cy="50%" r="65%">
          <stop offset="0%" stopColor="#5C5C5C" />
          <stop offset="60%" stopColor="#545454" />
          <stop offset="100%" stopColor="#3E3E3E" />
        </radialGradient>
        <radialGradient id="glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#48CAE4" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#48CAE4" stopOpacity="0" />
        </radialGradient>
        <filter id="soft" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="0.4" />
        </filter>
      </defs>

      <rect width="1200" height="700" fill="url(#vignette)" />

      {/* Subtle math grid */}
      <g stroke="#FFFFFF" strokeWidth="0.4" opacity="0.04">
        {Array.from({ length: 13 }).map((_, i) => (
          <line key={`v${i}`} x1={(i * 1200) / 12} y1="0" x2={(i * 1200) / 12} y2="700" />
        ))}
        {Array.from({ length: 8 }).map((_, i) => (
          <line key={`h${i}`} x1="0" y1={(i * 700) / 7} x2="1200" y2={(i * 700) / 7} />
        ))}
      </g>

      {/* Soft cyan signal pocket */}
      <circle cx="860" cy="220" r="240" fill="url(#glow)" opacity="0.5" />

      {/* Stars / signal points */}
      <g filter="url(#soft)">
        {points.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={p.r}
            fill={p.bright ? "#FFFFFF" : "#E8E8E8"}
            opacity={p.opacity}
          />
        ))}
      </g>

      {/* A few accent cyan signals */}
      {points
        .filter((p) => p.bright)
        .slice(0, 6)
        .map((p, i) => (
          <circle
            key={`a${i}`}
            cx={p.x}
            cy={p.y}
            r={p.r + 1.2}
            fill="none"
            stroke="#48CAE4"
            strokeWidth="0.5"
            opacity="0.35"
          />
        ))}
    </svg>
  );
}
